import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Box,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import ReceiptIcon from '@material-ui/icons/Receipt';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EventIcon from '@material-ui/icons/Event';
import HistoryIcon from '@material-ui/icons/History';
import ScheduleIcon from '@material-ui/icons/Schedule';
import PaymentIcon from '@material-ui/icons/Payment';
import TimerIcon from '@material-ui/icons/Timer';

import useApi from '../../../utils/useApi';
import UIContainer from '../../../components/UI/Container/UIContainer';
import UIFormTitle from '../../../components/UI/Form/Title/UIFormTitle';
import StoreContext from '../../../components/Store/context';
import { formattedCurrency } from '../../../utils/utils';
import PageContent from '../../../components/Main/Content/mainContent';
import AccessRestricted from '../../../components/Error/AccessRestricted/AccessRestricted';
import { isManagerOrAbove } from '../../../utils/permissionUtils';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 16,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      borderRadius: 12,
    },
  },
  glassPanel: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginTop: theme.spacing(0),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      borderRadius: 12,
    },
  },
  darkGlassPanel: {
    background: 'rgba(30, 30, 30, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginTop: theme.spacing(0),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      borderRadius: 12,
    },
  },
  aiButton: {
    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
    border: 0,
    borderRadius: 3,
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
    fontWeight: 'bold',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
  // KPI Card styling
  kpiCard: {
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    background: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 1.5),
      gap: theme.spacing(1),
    },
  },
  kpiIconBox: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      width: 36,
      height: 36,
      borderRadius: 8,
    },
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: '1.15rem',
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.95rem',
    },
  },
  kpiLabel: {
    fontSize: '0.72rem',
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.65rem',
    },
  },
  // Health score bar
  healthBar: {
    height: 10,
    borderRadius: 5,
  },
  healthBarBg: {
    borderRadius: 5,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  },
  // Profiles section
  profileSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: theme.spacing(1),
  },
  // AI Response
  aiResponseBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: '8px',
    background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
  },
  copyBtn: {
    marginTop: theme.spacing(1),
  },
  // Sale table rows
  saleRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  // Customer header
  customerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  customerAvatar: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #2196F3, #21CBF3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1.4rem',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      width: 44,
      height: 44,
      borderRadius: 10,
      fontSize: '1.1rem',
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Mobile-safe table wrapper
  mobileTableWrap: {
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    margin: '0 -4px',
    padding: '0 4px',
  },
}));

const KpiCard = ({ icon, iconBg, label, value, valueColor, tooltip }) => {
  const classes = useStyles();
  const card = (
    <div className={classes.kpiCard}>
      <div className={classes.kpiIconBox} style={{ background: iconBg }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography className={classes.kpiValue} style={valueColor ? { color: valueColor } : {}}>
          {value}
        </Typography>
        <Typography className={classes.kpiLabel}>{label}</Typography>
      </div>
    </div>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{card}</Tooltip> : card;
};

const AnaliseCliente = () => {
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { credencials } = useContext(StoreContext);

  const [customerData, setCustomerData] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  const [loadInfo, info] = useApi({
    method: 'get',
    url: `/customers/${id}/analysis`,
    onCompleted: (res) => {
      if (res && res.data) {
        setCustomerData(res.data);
        if (res.data.aiAnalysis) setAiSuggestions(res.data.aiAnalysis);
      }
    },
  });

  const [loadAi, aiInfo] = useApi({
    method: 'post',
    url: `/duxia/agents/customers-analysis/${id}`,
    onCompleted: (res) => {
      if (res && res.data && res.data.summary) setAiSuggestions(res.data);
    },
  });

  useEffect(() => {
    loadInfo();
  }, [id]);

  const handleRunDuxIA = () => {
    loadAi();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Mensagem copiada!');
  };

  if (info.loading && !customerData) {
    return (
      <UIContainer container justify="center" alignItems="center" style={{ minHeight: '50vh' }}>
        <CircularProgress />
      </UIContainer>
    );
  }

  if (info.error) {
    return (
      <UIContainer container>
        <Typography color="error">Erro ao carregar dados do cliente.</Typography>
        <Button onClick={() => history.goBack()}>Voltar</Button>
      </UIContainer>
    );
  }

  const {
    customer, profiles, totalRevenue, totalPromissory, totalDiscounts, ticketAvg, pendingCount, salesHistory, aiAnalysis,
    recencyDays, lifetimeDays, avgPurchaseInterval, avgPaymentInterval, most_buyed_items
  } = customerData || {};

  const currentAi = aiSuggestions || aiAnalysis;

  // Calculate health score (0-100): penalize unpaid, reward revenue
  const calcHealthScore = () => {
    if (!totalRevenue || totalRevenue === 0) return 50; // neutral for new customers
    const paidRatio = Math.max(0, 1 - (totalPromissory || 0) / totalRevenue);
    const pendingPenalty = Math.min(pendingCount || 0, 10) * 3; // up to 30 pts penalty
    const score = Math.round(paidRatio * 100 - pendingPenalty);
    return Math.max(0, Math.min(100, score));
  };

  const healthScore = calcHealthScore();
  const healthColor = healthScore >= 70 ? '#4caf50' : healthScore >= 40 ? '#f39708' : '#D60429';
  const healthLabel = healthScore >= 70 ? 'Saudável' : healthScore >= 40 ? 'Atenção' : 'Crítico';

  const customerInitials = customer ? `${(customer.name || '')[0] || ''}${(customer.lastName || '')[0] || ''}`.toUpperCase() : '?';

  const closedSales = salesHistory?.filter(s => s.isClosed)?.length || 0;
  const openSales = salesHistory?.filter(s => !s.isClosed)?.length || 0;

  const isManager = isManagerOrAbove(credencials);

  if (!isManager) {
    return <AccessRestricted requiredLevel="Gerente" />;
  }

  return (
    <PageContent>
      <UIContainer container direction="row">
        <Grid item xs={12}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()} style={{ marginBottom: 16 }}>
            Voltar para Pesquisa
          </Button>
        </Grid>

        <Grid item xs={12}>
          <UIFormTitle title={`Análise de Cliente`} />
        </Grid>

        <Grid container spacing={isMobile ? 2 : 3}>
          {/* LEFT COLUMN — Data & History */}
          <Grid item xs={12} md={7}>
            <Paper className={classes.paper} elevation={2}>
              {/* Customer Header */}
              <div id="tour-analysis-header" className={classes.customerHeader}>
                <div className={classes.customerAvatar}>{customerInitials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} style={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {customer?.name} {customer?.lastName || ''}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {[customer?.city, customer?.neighborhood].filter(Boolean).join(' · ') || 'Sem localização'}
                    {customer?.whatsapp ? ` · ${customer.whatsapp}` : ''}
                  </Typography>
                </div>
                {profiles && profiles.length > 0 && (
                  <div className={classes.profileSection}>
                    {profiles.map(p => (
                      <Chip
                        key={p.id}
                        label={p.name}
                        size="small"
                        style={{
                          backgroundColor: Number(p.type) === 2 ? '#D60429' : '#2196f3',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Health Score */}
              <Box id="tour-analysis-health" mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Saúde do Cliente
                  </Typography>
                  <Chip
                    label={`${healthScore}% — ${healthLabel}`}
                    size="small"
                    style={{
                      backgroundColor: healthColor,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                    }}
                    icon={healthScore >= 70 ? <CheckCircleIcon style={{ color: 'white', fontSize: 16 }} /> : <WarningIcon style={{ color: 'white', fontSize: 16 }} />}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={healthScore}
                  classes={{ root: classes.healthBarBg, bar: classes.healthBar }}
                  style={{ height: 10, borderRadius: 5 }}
                />
                <Box
                  className={classes.healthBar}
                  style={{
                    marginTop: -10,
                    width: `${healthScore}%`,
                    background: `linear-gradient(90deg, ${healthColor}CC, ${healthColor})`,
                    borderRadius: 5,
                  }}
                />
              </Box>

              {/* KPI Grid */}
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                <ReceiptIcon fontSize="small" color="primary" /> Métricas Financeiras
              </Typography>
              <Grid id="tour-analysis-financial-kpis" container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<TrendingUpIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg, #4caf50, #66bb6a)"
                    label="Faturamento Total"
                    value={formattedCurrency(totalRevenue || 0)}
                    valueColor="#4caf50"
                    tooltip="Soma de todas as vendas registradas para este cliente"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<ShoppingCartIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg, #2196f3, #42a5f5)"
                    label="Ticket Médio"
                    value={formattedCurrency(ticketAvg || 0)}
                    valueColor="#2196f3"
                    tooltip="Valor médio por compra"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<MoneyOffIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg, #D60429, #D60429)"
                    label="Valor Fiado"
                    value={formattedCurrency(totalPromissory || 0)}
                    valueColor={totalPromissory > 0 ? '#D60429' : undefined}
                    tooltip="Valor total em aberto (não pago)"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<LocalOfferIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg, #f39708, #f39708)"
                    label="Descontos Recebidos"
                    value={formattedCurrency(totalDiscounts || 0)}
                    tooltip="Total acumulado de descontos dados a este cliente"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<ReceiptIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg, #9c27b0, #ba68c8)"
                    label="Total de Compras"
                    value={salesHistory?.length || 0}
                    tooltip={`${closedSales} fechadas · ${openSales} pendentes`}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiCard
                    icon={<TrendingDownIcon style={{ color: 'white', fontSize: 22 }} />}
                    iconBg={`linear-gradient(135deg, ${pendingCount > 0 ? '#D60429' : '#4caf50'}, ${pendingCount > 0 ? '#D60429' : '#66bb6a'})`}
                    label="Vendas Pendentes"
                    value={pendingCount || 0}
                    valueColor={pendingCount > 0 ? '#D60429' : '#4caf50'}
                    tooltip="Vendas que ainda não foram fechadas"
                  />
                </Grid>
              </Grid>

              {/* Behavioral Metrics Grid */}
              <Box mt={4}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  <TimerIcon fontSize="small" color="primary" /> Métricas Temporais e Comportamentais
                </Typography>
                <Grid id="tour-analysis-temporal-kpis" container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <KpiCard
                      icon={<HistoryIcon style={{ color: 'white', fontSize: 22 }} />}
                      iconBg="linear-gradient(135deg, #607d8b, #90a4ae)"
                      label="Última Compra"
                      value={recencyDays === 0 ? 'Hoje' : `${recencyDays} dias`}
                      tooltip="Dias desde o último pedido realizado"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiCard
                      icon={<ScheduleIcon style={{ color: 'white', fontSize: 22 }} />}
                      iconBg="linear-gradient(135deg, #795548, #a1887f)"
                      label="Tempo de Casa"
                      value={`${lifetimeDays} dias`}
                      tooltip="Dias desde o primeiro pedido registrado"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiCard
                      icon={<EventIcon style={{ color: 'white', fontSize: 22 }} />}
                      iconBg="linear-gradient(135deg, #424242, #757575)"
                      label="Intervalo de Compra"
                      value={avgPurchaseInterval > 0 ? `${avgPurchaseInterval} dias` : 'N/A'}
                      tooltip="Média de dias entre compras consecutivas"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiCard
                      icon={<PaymentIcon style={{ color: 'white', fontSize: 22 }} />}
                      iconBg="linear-gradient(135deg, #3f51b5, #7986cb)"
                      label="Freq. de Pagamento"
                      value={avgPaymentInterval > 0 ? `${avgPaymentInterval} dias` : 'N/A'}
                      tooltip="Intervalo médio entre pagamentos realizados"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Top Purchased Items */}
              {most_buyed_items && most_buyed_items.length > 0 && (
                <Box id="tour-analysis-top-items" mt={4}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    <LocalOfferIcon fontSize="small" color="primary" /> Top Itens Comprados
                  </Typography>
                  <TableContainer className={classes.mobileTableWrap} style={{ maxHeight: 280, borderRadius: 8 }}>
                    <Table size="small" stickyHeader style={{ minWidth: isMobile ? 380 : undefined }}>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: 40 }}>#</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell style={{ width: 90 }}>Tipo</TableCell>
                          <TableCell align="right" style={{ width: 80 }}>Qtd.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {most_buyed_items.map((item, idx) => {
                          const medals = ['🥇', '🥈', '🥉'];
                          const isProduct = item.type === 'Produto';
                          return (
                            <TableRow key={`${item.type}-${item.name}`} className={classes.saleRow}>
                              <TableCell>
                                <Typography variant="body2" style={{ fontWeight: idx < 3 ? 700 : 400, fontSize: idx < 3 ? '1.1rem' : undefined }}>
                                  {idx < 3 ? medals[idx] : idx + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" style={{ fontWeight: idx < 3 ? 600 : 400 }}>
                                  {item.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.type}
                                  size="small"
                                  style={{
                                    backgroundColor: isProduct ? '#2196f3' : '#9c27b0',
                                    color: 'white',
                                    fontSize: '0.68rem',
                                    fontWeight: 'bold',
                                    height: 22,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {Number.isInteger(item.totalQtd) ? item.totalQtd : item.totalQtd.toFixed(1)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Sales History Table */}
              <Box id="tour-analysis-history" mt={4}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  <ShoppingCartIcon fontSize="small" color="primary" /> Histórico de Compras
                </Typography>
                <TableContainer className={classes.mobileTableWrap} style={{ maxHeight: 340, borderRadius: 8 }}>
                  <Table size="small" stickyHeader style={{ minWidth: isMobile ? 480 : undefined }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Pedido</TableCell>
                        <TableCell>Data/Hora</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Pago</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesHistory?.map((sale) => (
                        <TableRow key={sale.saleId} className={classes.saleRow}>
                          <TableCell>#{sale.saleId}</TableCell>
                          <TableCell>{sale.moment}</TableCell>
                          <TableCell align="right">{formattedCurrency(sale.value)}</TableCell>
                          <TableCell align="right">{formattedCurrency(sale.amountPaid)}</TableCell>
                          <TableCell align="center">
                            {sale.isClosed ? (
                              <Chip label="Paga" size="small" style={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.7rem' }} />
                            ) : sale.amountPaid > 0 ? (
                              <Chip label="Parcial" size="small" style={{ backgroundColor: '#f39708', color: 'white', fontSize: '0.7rem' }} />
                            ) : (
                              <Chip label="Pendente" size="small" style={{ backgroundColor: '#D60429', color: 'white', fontSize: '0.7rem' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!salesHistory || salesHistory.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">Nenhuma compra registrada.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT COLUMN — AI Panel */}
          <Grid item xs={12} md={5}>
            <div id="tour-analysis-ai" className={document.body.className.includes('dark') ? classes.darkGlassPanel : classes.glassPanel}>
              <Grid container alignItems="center" spacing={2} style={{ marginBottom: 16 }}>
                <Grid item>
                  <SupervisedUserCircleIcon style={{ fontSize: 40, color: '#2196F3' }} />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" style={{ fontWeight: 600 }}>Dux IA</Typography>
                  <Typography variant="body2">Agente de Retenção</Typography>
                </Grid>
              </Grid>

              <Typography variant="body1" paragraph>
                Utilize a inteligência artificial para ler o histórico deste cliente, identificar o padrão de consumo, e elaborar uma estratégia de fidelização ou cobrança amigável.
              </Typography>

              <Button
                className={classes.aiButton}
                fullWidth
                startIcon={aiInfo.loading ? <CircularProgress size={20} color="inherit" /> : <AutorenewIcon />}
                onClick={handleRunDuxIA}
                disabled={aiInfo.loading}
              >
                {aiInfo.loading ? 'Analisando Histórico...' : currentAi ? 'Reanalisar Histórico' : 'Gerar Análise e Estratégia'}
              </Button>

              {aiInfo.error && (
                <Typography color="error" style={{ marginTop: 16 }}>
                  Erro ao gerar análise. Verifique se você possui mensagens disponíveis na sua cota mensal.
                </Typography>
              )}

              {currentAi && (
                <div className={classes.aiResponseBox}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>📝 Resumo do Relacionamento</Typography>
                  <Typography variant="body2" paragraph>{currentAi.summary}</Typography>

                  <Divider style={{ margin: '16px 0' }} />

                  <Typography variant="subtitle2" color="primary" gutterBottom>💬 Sugestão de Mensagem (WhatsApp)</Typography>
                  <Typography variant="body2" style={{ fontStyle: 'italic', background: 'rgba(0,0,0,0.05)', padding: 8, borderRadius: 4 }}>
                    "{currentAi.message_suggestion}"
                  </Typography>
                  <Button size="small" color="primary" className={classes.copyBtn} onClick={() => copyToClipboard(currentAi.message_suggestion)}>
                    Copiar Mensagem
                  </Button>

                  <Divider style={{ margin: '16px 0' }} />

                  <Typography variant="subtitle2" color="primary" gutterBottom>🏷️ Sugestão de Desconto/Oferta</Typography>
                  <Typography variant="body2">{currentAi.discount_suggestion}</Typography>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </UIContainer>
    </PageContent>
  );
};

export default AnaliseCliente;
